/**
 * LiveTowRequests — the "live tow requests" panel from mockup `03-web-dispatch`
 * screen 1.
 *
 * A compact feed of the most recent open requests so dispatch can see what is
 * happening without leaving the dashboard. Each row links to the request.
 *
 * The mockup pairs this panel with a map of the same requests. `Get-List-Admin`
 * returns no coordinates (see `GetListTowRequestDTO`), and the only place
 * lat/lng exists is the per-request detail — so plotting the feed would mean
 * one detail fetch per row on every dashboard load. The panel ships without the
 * map rather than paying that cost or faking pins; see OPEN_QUESTIONS Q7.
 *
 * Deliberately does NOT send `isDashboard`. That flag narrows the query to
 * requests with no provider attached, which is not what a general feed wants —
 * and on the ADMIN branch it dereferences `startDate.Value` unguarded
 * (`TowRequestBLL.cs:271`), so sending it without a date range 500s.
 */
import { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { towRequestsApi, fromAxiosError } from '@/core/api';
import type { GetListTowRequestDTO } from '@/core/api';
import { EmptyState, StatusChip, useFormattedDate } from '@/shared';
import { relativeTime } from '@/core/utils';

/** How many rows the panel shows. Enough to be useful, short enough to scan. */
const ROW_LIMIT = 8;

/**
 * Detail route for a row. The tow-request detail reads `serviceType` off the
 * query string, so it's carried through here exactly as the tow-requests list
 * does — otherwise arriving from the dashboard loses it.
 */
function detailPath(row: GetListTowRequestDTO): string {
  return `/tow-requests/${row.requestTowId}?serviceType=${encodeURIComponent(row.serviceType ?? '')}`;
}

export default function LiveTowRequests() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatLocalized = useFormattedDate();

  const [rows, setRows] = useState<GetListTowRequestDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorMessage(null);

    towRequestsApi
      .getTowRequestsList({
        status: 'OPEN',
        page: 0,
        pageSize: ROW_LIMIT,
        orderBy: 'requestDate',
        orderDirection: 'desc',
      })
      .then((result) => {
        // Server-side pageSize should already cap this; the slice is a cheap
        // guarantee of the panel's contract regardless.
        if (!cancelled) setRows((result.elements ?? []).slice(0, ROW_LIMIT));
      })
      .catch((err: unknown) => {
        if (!cancelled) setErrorMessage(fromAxiosError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* Panel header — mockup's .panel .ph */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1.625,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography component="h2" sx={{ fontSize: '0.84375rem', fontWeight: 800 }}>
          {t('HOME.liveTowRequests.title')}
        </Typography>
        <MuiLink
          component={RouterLink}
          to="/tow-requests?status=OPEN"
          underline="hover"
          sx={{ fontSize: '0.71875rem', fontWeight: 700, color: 'secondary.main' }}
        >
          {t('HOME.liveTowRequests.viewAll')}
        </MuiLink>
      </Box>

      {loading ? (
        <Box sx={{ p: 2 }} aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="text" sx={{ fontSize: '1.5rem', mb: 0.5 }} />
          ))}
        </Box>
      ) : errorMessage !== null ? (
        <EmptyState
          title={t('HOME.liveTowRequests.errorTitle')}
          description={errorMessage}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('HOME.liveTowRequests.emptyTitle')}
          description={t('HOME.liveTowRequests.emptyBody')}
        />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('TOWING-REQUEST.claimNumber')}</TableCell>
                <TableCell>{t('TOWING-REQUEST.customer')}</TableCell>
                <TableCell>{t('TOWING-REQUEST.towProvider')}</TableCell>
                <TableCell>{t('TOWING-REQUEST.status')}</TableCell>
                <TableCell>{t('TOWING-REQUEST.requestDate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.requestTowId}
                  hover
                  // Whole row is the target, matching the tow-requests list.
                  // The claim-number link alone was a tiny hit area that didn't
                  // read as clickable. `serviceType` rides along in the query
                  // string because the detail page reads it, same as the list.
                  sx={{
                    cursor: 'pointer',
                    '&:last-child td': { borderBottom: 'none' },
                  }}
                  onClick={() => navigate(detailPath(row))}
                >
                  <TableCell sx={{ py: 1.375, px: 2, fontWeight: 800 }}>
                    {/* Kept as a real link so the row stays keyboard-reachable
                        and supports middle-click / open-in-new-tab. The click
                        is stopped from bubbling so it doesn't also fire the
                        row handler and navigate twice. */}
                    <MuiLink
                      component={RouterLink}
                      to={detailPath(row)}
                      underline="hover"
                      color="inherit"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.claimNumber || `#${row.requestTowId}`}
                    </MuiLink>
                  </TableCell>
                  <TableCell sx={{ py: 1.375, px: 2 }}>{row.nameCustomer || '—'}</TableCell>
                  <TableCell sx={{ py: 1.375, px: 2 }}>
                    {row.nameTowProvider || (
                      // No provider yet is the state that needs a human — say so
                      // rather than rendering an ambiguous dash.
                      <Typography component="span" sx={{ fontSize: '0.6875rem', color: 'warning.main', fontWeight: 700 }}>
                        {t('HOME.liveTowRequests.unassigned')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.375, px: 2 }}>
                    <StatusChip
                      status={row.status}
                      // Same namespace the tow-requests list uses, so a status
                      // reads identically here and there.
                      label={t(`STATUS-REPORT-SERVICES.${row.status}`, { defaultValue: row.status })}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.375, px: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {row.requestDate ? (
                      <Tooltip title={formatLocalized(row.requestDate)}>
                        <span>{relativeTime(row.requestDate) ?? formatLocalized(row.requestDate)}</span>
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
